import { AlertTriangle, Zap } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { TOOLBAR_CONTROL_SHELL, TOOLBAR_CONTROL_ICON, TOOLBAR_CONTROL_LABEL } from './toolbarControlClasses';
import type { AutoDraftState } from '@/hooks/useNFLAutoDraft';

/**
 * SAB-881: the autodraft control, in the centre column's toolbar.
 *
 * ── WHY IT MOVED ────────────────────────────────────────────────────────────
 *
 * It used to live in the body of the QUEUE tab, which meant it was invisible unless QUEUE
 * happened to be the selected rail tab -- a standing delegation of every remaining pick,
 * hidden behind a tab. The toolbar is wider, has no unread badges competing for attention,
 * and is not the column that loses its last tab to a horizontal overflow.
 *
 * ── A CONTROL, NOT A THIRD VIEW ─────────────────────────────────────────────
 *
 * It sits immediately left of the PLAYERS / BOARD toggle, which is the risk: `--primary` IS
 * orange (hsl 25 95% 53%), and the toggle's active segment is a solid `bg-primary` fill. So
 * ON uses orange as an ACCENT -- border, icon, label, switch -- and never as a fill, or it
 * would read as a third segment of that toggle.
 *
 * The stronger separation is semantic and cannot be undone by styling: this is a `Switch`
 * (role="switch", aria-checked), the toggle is role="tab"/aria-selected. A switch cannot be
 * mistaken for a view tab by anything that reads the page.
 *
 * ── FOUR STATES, AND WHY `null` IS ONE OF THEM ──────────────────────────────
 *
 * `enabled` is `boolean | null`. `null` means the flag has not been read yet or the read
 * FAILED -- never "off". Rendering it as off would show a flagged manager a switch in the
 * off position while the server keeps taking their picks, which is the collapse-unknown
 * mistake #300 deliberately avoided. It gets its own tint, its own icon and the explanation
 * in `title`, because a toolbar has no room for the sentence the rail used to carry.
 *
 * OFF MUST NOT LOOK DISABLED. Muted text plus a faint border is the disabled vocabulary;
 * off is idle but live. It keeps full-contrast foreground, a solid border, and an enabled
 * switch. Only SAVING is actually disabled.
 */

/** The sentence the rail used to render inline. Kept verbatim; a toolbar shows it on hover. */
export const AUTODRAFT_UNKNOWN_TITLE =
  'We could not read your autodraft setting. Switch it off to be sure.';

export interface AutoDraftControlProps {
  /** `null` = unknown. Never coerce it to false. */
  enabled: AutoDraftState;
  saving?: boolean;
  onChange: (next: boolean) => void;
  /**
   * SAB-959: 'pill' is the compact header form for the mobile room; 'toolbar' is the
   * original and stays the default, so the desktop control and its tests are untouched.
   *
   * A VARIANT, NOT A SECOND COMPONENT. The four-state `null` handling above is the whole
   * reason this file exists, and a separate pill would have had to reimplement it -- which
   * is how a flagged manager ends up seeing "off" in one room and "unknown" in the other.
   * Both forms therefore emit the same `data-autodraft` and the same test ids.
   */
  variant?: 'toolbar' | 'pill';
}

export default function AutoDraftControl({
  enabled, saving, onChange, variant = 'toolbar',
}: AutoDraftControlProps) {
  const unknown = enabled === null;
  const on = enabled === true;

  if (variant === 'pill') {
    return (
      <button
        type="button"
        data-testid="auto-draft-row"
        data-autodraft={unknown ? 'unknown' : String(on)}
        role="switch"
        aria-checked={on}
        aria-label="Auto-draft the rest of my picks"
        title={unknown ? AUTODRAFT_UNKNOWN_TITLE : undefined}
        disabled={saving}
        onClick={() => onChange(!on)}
        className={cn(
          // The design asks for a 36px pill with a padded 44px touch area. This app already
          // guarantees the second half globally -- src/index.css:411 floors every button at
          // min-height/min-width 44px, with `[data-compact]` as the opt-out. So the pill
          // renders 44px tall rather than 36, and NO ::after hit-area is needed; adding one
          // was belt-and-braces over a rule that already holds. Measured 44x123 on device.
          'inline-flex flex-none items-center gap-[6px] rounded-full border px-[10px]',
          on && 'border-primary bg-primary/[0.16] text-primary',
          !on && !unknown && 'border-border text-muted-foreground',
          unknown && 'border-gold/60 bg-gold/[0.10] text-gold',
          saving && 'opacity-60',
        )}
      >
        {/* The design drops the icon for space, so UNKNOWN cannot be told from OFF by the
            tint alone -- the exact hue-only failure the header comment warns about. The
            wording carries it instead: "AUTODRAFT ?" rather than "AUTODRAFT". */}
        <span className="font-display text-[10.5px] font-bold tracking-[0.08em]">
          {unknown ? 'AUTODRAFT ?' : 'AUTODRAFT'}
        </span>
        <span
          data-testid="auto-draft-toggle"
          aria-hidden="true"
          className={cn(
            'flex h-[15px] w-[26px] flex-none items-center rounded-full p-0.5 transition-colors duration-[180ms] ease-out',
            on ? 'bg-primary' : 'bg-muted',
          )}
        >
          <span
            className={cn(
              'h-[11px] w-[11px] rounded-full transition-transform duration-[180ms] ease-out',
              on ? 'translate-x-[11px] bg-primary-foreground' : 'translate-x-0 bg-muted-foreground',
            )}
          />
        </span>
      </button>
    );
  }

  return (
    <div
      data-testid="auto-draft-row"
      // Preserved verbatim from the rail so the existing SAB-853 assertions keep their
      // meaning after the move: 'true' | 'false' | 'unknown', never a coerced boolean.
      data-autodraft={unknown ? 'unknown' : String(on)}
      title={unknown ? AUTODRAFT_UNKNOWN_TITLE : undefined}
      className={cn(
        TOOLBAR_CONTROL_SHELL,
        on && 'border-primary/55 bg-primary/[0.10]',
        // OFF: solid border, ordinary surface. Deliberately NOT `border-dashed`,
        // `opacity-*` or a muted surface -- see the note above.
        !on && !unknown && 'border-border bg-muted/40',
        unknown && 'border-gold/60 bg-gold/[0.10]',
      )}
    >
      {unknown ? (
        <AlertTriangle className={cn(TOOLBAR_CONTROL_ICON, 'text-gold')} />
      ) : (
        <Zap className={cn(TOOLBAR_CONTROL_ICON, on ? 'text-primary' : 'text-foreground')} />
      )}

      <label
        htmlFor="auto-draft-toggle"
        className={cn(
          TOOLBAR_CONTROL_LABEL,
          // Distinct on THREE channels -- colour, icon and wording -- so the states are not
          // told apart by hue alone.
          on ? 'text-primary' : unknown ? 'text-gold' : 'text-foreground',
        )}
      >
        {on ? 'Autodraft is ON' : unknown ? 'Autodraft ?' : 'Autodraft'}
      </label>

      <Switch
        id="auto-draft-toggle"
        data-testid="auto-draft-toggle"
        aria-label="Auto-draft the rest of my picks"
        aria-describedby={undefined}
        disabled={saving}
        // `checked` is false when unknown, but the surrounding tint, icon and label say
        // "unknown" rather than "off" -- the switch alone is not carrying that meaning.
        checked={on}
        onCheckedChange={onChange}
      />
    </div>
  );
}
